import { Marked, Parser, Tokens, Renderer, Token } from 'marked';
import markedKatex from 'marked-katex-extension';
import { markedTexMath } from './texMathExtension';
import { REGEX } from '../textProcessing';

// =============================================================================
// MARKDOWN RENDERING
// =============================================================================

// Helper function to parse and clean text within renderer context
function parseAndClean(this: Renderer, tokens: Token[], inline = true): string {
    const parser = this.parser as Parser;
    const text = inline ? parser.parseInline(tokens) : parser.parse(tokens);
    return text.trim().replace(REGEX.TRAILING_PUNCTUATION, '');
}

const ttsRenderer = {
    // --- Block-level Elements ---
    heading(this: Renderer, { tokens, depth }: Tokens.Heading): string {
        const text = parseAndClean.call(this, tokens);
        return text ? `Heading ${depth}: ${text}. ` : '';
    },

    listitem(this: Renderer, { tokens }: Tokens.ListItem): string {
        const text = parseAndClean.call(this, tokens, false);
        return `List item: ${text}. `;
    },

    table(this: Renderer, { header, rows }: Tokens.Table): string {
        const parseRow = (r: Tokens.TableCell[]) =>
            r.map((c) => parseAndClean.call(this, c.tokens)).join(', ');
        const headerText = `Table with headers: ${parseRow(header)}. `;
        const rowsText = rows
            .map((row, i) => `Row ${i + 1}: ${parseRow(row)}. `)
            .join('');
        return `${headerText}${rowsText}`;
    },

    blockquote(this: Renderer, { tokens }: Tokens.Blockquote): string {
        const text = parseAndClean.call(this, tokens, false);
        return text ? `Quote: ${text}. ` : '';
    },

    code (this: Renderer, { text, lang }: Tokens.Code): string {
        const langDesc = lang || 'an unspecified language';
        return `Code block in ${langDesc}: ${text}. `;
    },

    paragraph(this: Renderer, { tokens }: Tokens.Paragraph): string {
        const text = (this.parser as Parser).parseInline(tokens).trim();
        return text ? `${text} ` : '';
    },

    hr(): string {
        return '. ';
    },

    // --- Inline-level Elements ---
    strong(this: Renderer, { tokens }: Tokens.Strong): string {
        return (this.parser as Parser).parseInline(tokens);
    },

    em(this: Renderer, { tokens }: Tokens.Em): string {
        return (this.parser as Parser).parseInline(tokens);
    },

    del(this: Renderer, { tokens }: Tokens.Del): string {
        return (this.parser as Parser).parseInline(tokens);
    },

    codespan({ text }: Tokens.Codespan): string {
        return text;
    },

    link(this: Renderer, { text }: Tokens.Link): string {
        return text;
    },

    image({ text }: Tokens.Image): string {
        return `Image: ${text || 'an image'}.`;
    },

    br(): string {
        return ' . ';
    },

    html(this: Renderer, token: Tokens.HTML | Tokens.Tag): string {
        return token.text.trim() === '<br>' ? ' . ' : '';
    },
};

// Exported Marked instance using ttsRenderer for TTS generation
export const ttsMarked = new Marked({
    renderer: ttsRenderer,
    useNewRenderer: true,
    gfm: true,
});

ttsMarked.use(markedKatex({ throwOnError: false }));
ttsMarked.use(markedTexMath());

ttsMarked.use({
    extensions: [
        {
            name: 'blockKatex',
            level: 'block' as const,
            renderer(token: any): string { return `${token.text} `; },
        },
        {
            name: 'inlineKatex',
            level: 'inline' as const,
            renderer(token: any): string { return `${token.text} `; },
        },
    ],
});
